import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField, Typography,
} from '@mui/material';
import { DATE_PRESETS, DEFAULT_DATE_PRESET, type DatePresetKey } from '../../utils/date-presets';
import { DEFAULT_GROUP_BY } from '../../constants/tracking-groupings';
import { SORTABLE_FIELDS } from '../../utils/user-settings';
import { PRESET_FIELDS, type PresetField, type TrackingPreset } from '../../utils/tracking-presets';
import { MAX_DRILLDOWN_LEVELS, levelDims, setLevelAt, usedElsewhere } from './tracking-page.format';
import LevelPicker from '../../components/LevelPicker';
import EntityFilterPicker, { FilterEntry } from '../../components/EntityFilterPicker';
import { ENTITY_FILTER_ORDER } from '../../components/entity-fetchers';
import TagSelect from '../../components/TagSelect';

const SORT_OPTIONS = [{ value: 'name', label: 'Name' }, ...SORTABLE_FIELDS];

interface Props {
  open: boolean;
  onClose: () => void;
  preset: TrackingPreset | null;
  vLabel: (field: string) => string;
  onSave: (preset: TrackingPreset) => void;
}

/** Edit a preset's fields directly (vs. the wholesale "update to current view"). */
export default function PresetEditDialog({ open, onClose, preset, vLabel, onSave }: Props) {
  const [name, setName] = useState('');
  const [fields, setFields] = useState<Set<PresetField>>(new Set());
  const [datePreset, setDatePreset] = useState<DatePresetKey>(DEFAULT_DATE_PRESET);
  const [groupBy, setGroupBy] = useState<string>(DEFAULT_GROUP_BY);
  const [drilldowns, setDrilldowns] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterEntry[]>([]);
  const [filterField, setFilterField] = useState<string>(ENTITY_FILTER_ORDER[0]);
  const [tags, setTags] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // seed on open only (re-seeding mid-edit would discard the user's changes)
  useEffect(() => {
    if (!open || !preset) return;
    setName(preset.name);
    const f = new Set<PresetField>();
    if (preset.date_preset) f.add('date');
    if (preset.group_by !== undefined) f.add('group');
    if (preset.drilldowns !== undefined) f.add('drilldown');
    if (preset.filters !== undefined) f.add('filters');
    if (preset.tags !== undefined) f.add('tags');
    if (preset.sort) f.add('sort');
    setFields(f);
    setDatePreset(preset.date_preset ?? DEFAULT_DATE_PRESET);
    setGroupBy(preset.group_by ?? DEFAULT_GROUP_BY);
    setDrilldowns(preset.drilldowns ?? []);
    setFilters(preset.filters ?? []);
    setFilterField(preset.filters?.[0]?.field ?? ENTITY_FILTER_ORDER[0]);
    setTags(preset.tags ?? []);
    setSortField(preset.sort?.field ?? 'name');
    setSortOrder(preset.sort?.order ?? 'desc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key: PresetField) => setFields((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const canSave = name.trim().length > 0 && fields.size > 0;

  const handleSave = () => {
    if (!preset) return;
    const out: TrackingPreset = { id: preset.id, name: name.trim() };
    if (fields.has('date')) out.date_preset = datePreset;
    if (fields.has('group')) out.group_by = groupBy;
    if (fields.has('drilldown')) out.drilldowns = drilldowns;
    if (fields.has('filters')) out.filters = filters;
    if (fields.has('tags')) out.tags = tags;
    if (fields.has('sort')) out.sort = { field: sortField, order: sortOrder };
    onSave(out);
    onClose();
  };

  const editor = (key: PresetField) => {
    switch (key) {
      case 'date':
        return (
          <TextField select size="small" fullWidth value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DatePresetKey)}>
            {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
          </TextField>
        );
      case 'group':
        return (
          <LevelPicker label="Group by" level={groupBy} required dimLabel={vLabel}
            used={drilldowns.flatMap(levelDims)} onChange={setGroupBy} />
        );
      case 'drilldown':
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[...drilldowns, ''].slice(0, MAX_DRILLDOWN_LEVELS).map((level, i) => (
              <LevelPicker
                key={i} label={i === 0 ? 'Drilldown' : `Level ${i + 1}`} level={level} dimLabel={vLabel}
                used={usedElsewhere(groupBy, drilldowns, i)}
                onChange={(next) => setDrilldowns((prev) => setLevelAt(prev, i, next))}
                sx={{ minWidth: 150, flex: '1 1 150px' }}
              />
            ))}
          </Box>
        );
      case 'filters':
        return (
          <EntityFilterPicker
            filters={filters} onFilters={setFilters}
            field={filterField} onField={setFilterField} labelForField={vLabel} fullWidth
          />
        );
      case 'tags':
        return <TagSelect value={tags} onChange={setTags} allowCreate={false} label="Tags" placeholder="All" sx={{ width: '100%' }} />;
      case 'sort':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField select size="small" sx={{ flex: 1 }} value={sortField}
              onChange={(e) => setSortField(e.target.value)}>
              {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" sx={{ width: 120 }} value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}>
              <MenuItem value="desc">High → low</MenuItem>
              <MenuItem value="asc">Low → high</MenuItem>
            </TextField>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Edit preset</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus fullWidth size="small" label="Preset name" value={name}
          onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          Tick a field to pin it, then set its value. Unticked fields are left off the preset.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {PRESET_FIELDS.map(({ key, label }) => (
            <Box key={key}>
              <FormControlLabel
                control={<Checkbox size="small" checked={fields.has(key)} onChange={() => toggle(key)} />}
                label={label}
              />
              {fields.has(key) && <Box sx={{ ml: 4, mb: 1 }}>{editor(key)}</Box>}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
